import { useState } from 'react';
import { Grid, Typography } from '@mui/material';
import dayjs from 'dayjs';
import StatusLabel from 'src/components/StatusLabel';
import { StatusTransactionLabelColor } from 'src/constants/status';
import { ITransactionsData } from 'src/interfaces/transactions';
import { getStatusTransactionLabel } from 'src/components/TransactionsComponent/utils/index';
import { formatMoney } from 'src/utils/global';
import { openDetailEvent } from 'src/analitycs/pagesEvents/transactions';

import DrawerInfoTransaction from '../DrawerInfoTransaction';

import TransactionIdCell from './TransactionIdCell';
import MenuActionsTransaction from './MenuActionsTransaction';
import { COLUMN_WIDTHS } from '../TableListTransactions/columns';

import { styles } from './styles';

interface Props {
  transaction: ITransactionsData;
}

/**
 * Fila de la tabla definitiva (H5): ID → Fecha y hora → Terminal → Monto → Tipo →
 * Bandera → Banco → Estado. Tipo/Bandera/Banco se renderizan tal cual vienen del
 * backend (sin mapping; la excepción documentada es el drawer, que conserva el suyo).
 * La fila es clickeable y abre el drawer de detalle (owner único del drawer acá).
 */
function ItemTransaction({ transaction }: Readonly<Props>) {
  const { classes } = styles();
  const [showDrawer, setShowDrawer] = useState<boolean>(false);
  const transactionPaymentStatus = transaction.status;
  const transactionPaymentStatusLabel = getStatusTransactionLabel(transactionPaymentStatus);
  // dayjs considera VÁLIDO el año 0000 (placeholder que a veces manda backend) → año mínimo.
  const MIN_VALID_YEAR = 1970;
  const parsedDate = dayjs(transaction?.date);
  const hasValidDate = !!transaction?.date && parsedDate.isValid() && parsedDate.year() >= MIN_VALID_YEAR;

  const handleOpenDrawer = () => {
    openDetailEvent(transaction.id);
    setShowDrawer(true);
  };

  return (
    <>
      {showDrawer && (
        <DrawerInfoTransaction showDrawer={showDrawer} setShowDrawer={setShowDrawer} transaction={transaction} />
      )}
      <Grid container className={classes.root} onClick={handleOpenDrawer} data-testid={`transaction-row-${transaction.id}`}>
        <Grid item xs={COLUMN_WIDTHS.id} className={classes.tableRow}>
          <TransactionIdCell transactionId={transaction?.id} />
        </Grid>

        <Grid item xs={COLUMN_WIDTHS.date} className={classes.tableRow}>
          <div className={classes.dateContainer}>
            <Typography noWrap>
              {hasValidDate ? parsedDate.format('DD/MM/YYYY') : '—'}
            </Typography>
            <Typography noWrap>
              {hasValidDate ? parsedDate.format('HH:mm:ss') : '—'}
            </Typography>
          </div>
        </Grid>

        <Grid item xs={COLUMN_WIDTHS.terminal} className={classes.tableRow}>
          <Typography fontSize={16} padding={1}>
            {transaction?.terminal?.id}
          </Typography>
        </Grid>

        <Grid item xs={COLUMN_WIDTHS.amount} className={classes.tableRow}>
          <Typography fontSize={16} padding={1} whiteSpace={'nowrap'}>
            {formatMoney(transaction?.amount)}
          </Typography>
        </Grid>

        <Grid item xs={COLUMN_WIDTHS.type} className={classes.tableRow}>
          <Typography fontSize={16} padding={1}>
            {transaction?.card?.type}
          </Typography>
        </Grid>

        <Grid item xs={COLUMN_WIDTHS.brand} className={classes.tableRow}>
          <Typography fontSize={16} padding={1}>
            {transaction?.card?.brand}
          </Typography>
        </Grid>

        <Grid item xs={COLUMN_WIDTHS.bank} className={classes.tableRow}>
          <Typography fontSize={16} padding={1}>
            {transaction?.bank?.name}
          </Typography>
        </Grid>

        <Grid item xs={COLUMN_WIDTHS.status} className={classes.tableRow}>
          <StatusLabel
            backgroundColor={StatusTransactionLabelColor[`${transactionPaymentStatus}`]}
            label={transactionPaymentStatusLabel}
          />
        </Grid>

        <Grid item xs={COLUMN_WIDTHS.actions} justifyContent="center" className={classes.tableRow}>
          <MenuActionsTransaction onOpenDetail={handleOpenDrawer} />
        </Grid>
      </Grid>
    </>
  );
}

export default ItemTransaction;
